/* eslint-disable */
import { LocatorText } from '@readium/shared';
import { resolveContentCfi } from './cfi';
import {
  collapseWhitespace,
  findQuoteOccurrence,
  removeWhitespace,
  type CollapsedText,
  type TextOffsetRange,
  type TextQuote,
} from './text';
import type { SelectionContext } from '../models/selections';

/**
 *
 * @returns An object containing:
 * - `text`: The `LocatorText` object with before, highlight, and after context.
 * - `contextReadingPoint`: A formatted string representing the reading context.
 */
export function getLocatorContextAndReadingPoint(
  frames: HTMLIFrameElement[],
  selectionContext?: SelectionContext,
): {
  text: LocatorText;
  contextReadingPoint: string;
} {
  const locatorText: LocatorText = selectionContext
    ? new LocatorText({
        before: selectionContext.before,
        highlight: selectionContext.selectedText,
        after: selectionContext.after,
      })
    : getFirstVisibleTextContext(frames);
  if (!frames.length) return { text: locatorText, contextReadingPoint: '' };
  const activeIframe = findVisibleIframe(frames) || frames[0];
  const selection = activeIframe.contentWindow?.getSelection();
  let context: LocatorText | string = '';
  let startContainer: Node | undefined;

  if (selection && !selection.isCollapsed && selection.rangeCount > 0) {
    const range = selection.getRangeAt(0);
    startContainer = range.startContainer;

    if (startContainer?.nodeType === Node.TEXT_NODE) {
      const textBeforeCursor = startContainer.textContent?.slice(0, range.startOffset)?.trim();
      context = textBeforeCursor || locatorText.before || '';
    }
  } else {
    const visibleText = getVisibleTextFragment(frames);
    context = visibleText.trim() || locatorText.before || '';
  }

  return { text: locatorText, contextReadingPoint: formatContext(context) };
}

/**
 * Finds every iframe actually occupying screen space (the active ones in Readium2), ordered
 * left to right. CSS `visibility` isn't a reliable signal here: Readium's fixed-layout navigator
 * sets `visibility: hidden` inline on every managed iframe, including the one(s) currently on
 * screen (likely a rendering artifact of how it composites columns/spreads), so checking it would
 * never find a match for those books and TTS/selection code would treat every chunk as invisible
 * and page-turn forever instead of ever speaking or reading a selection.
 *
 * A fixed-layout book rendered as a two-page spread puts TWO of these on screen at once, each
 * with a different resource loaded — a plural result (rather than the first DOM-order match) is
 * what lets callers account for the right-hand page instead of treating it as if it doesn't exist.
 * @param iframes - Array of available iframes
 * @returns The visible iframes, left to right
 */
export function findVisibleIframes(iframes: HTMLIFrameElement[]): HTMLIFrameElement[] {
  return iframes
    .filter((iframe) => {
      const rect = iframe.getBoundingClientRect();
      return (
        rect.width > 0 &&
        rect.height > 0 &&
        rect.bottom > 0 &&
        rect.right > 0 &&
        rect.top < window.innerHeight &&
        rect.left < window.innerWidth
      );
    })
    .sort((a, b) => a.getBoundingClientRect().left - b.getBoundingClientRect().left);
}

/**
 * Finds the primary iframe actually occupying screen space (the leftmost one, in a two-page
 * spread). See {@link findVisibleIframes} for why a plural form exists alongside this.
 * @param iframes - Array of available iframes
 * @returns The visible iframe or null
 */
export function findVisibleIframe(iframes: HTMLIFrameElement[]): HTMLIFrameElement | undefined {
  return findVisibleIframes(iframes)[0];
}

/**
 * Formats the reading context.
 * @param context - String containing the context to format
 * @returns Formatted string limited to the last 40 characters
 */
export function formatContext(context: string): string {
  return context.trim().replace(/\n/g, ' ').slice(-40);
}

/**
 * Extracts the full body text for speech, the same way `getVisibleTextFragment` does it:
 * walking text nodes rather than reading `textContent` directly. `textContent` concatenates
 * adjacent text nodes with no separator (e.g. a title split across sibling elements with no
 * whitespace between them in the source markup), which silently glues words together. That
 * glued text then never matches the space-separated text `getVisibleTextFragment` reconstructs
 * for the same content, so visibility-based chunk matching in TtsService fails and the chunk
 * (a title, or any paragraph with adjacent inline markup) is skipped instead of spoken.
 * @param body - The root element to extract text from (typically an iframe's document body).
 * @returns {string} The body's text, with a boundary space inserted only where two text nodes
 * would otherwise run together.
 */
export function getBodyTextForSpeech(body: HTMLElement): string {
  return walkTextWithSpans(body).text;
}

export interface BodyTextVisibleRange {
  text: string;
  visibleStart: number;
  visibleEnd: number;
}

/**
 * Like `getBodyTextForSpeech`, but also reports the [visibleStart, visibleEnd) range of the
 * returned text that's currently on screen. TtsService uses this instead of matching substrings
 * (see `getVisibleTextFragment`) to locate which chunk of a long chapter's full text is being
 * read: a chapter-wide text can repeat the same short phrase or name many times, so a substring
 * match can land on the wrong occurrence anywhere in the chapter. An offset is unambiguous.
 * @param body - The root element to walk (typically an iframe's document body).
 * @returns The body's text, plus the visible range within it (-1/-1 if nothing is visible).
 */
function getBodyTextWithVisibleRange(body: HTMLElement): BodyTextVisibleRange {
  const viewportBottom = window.innerHeight;
  const viewportRight = window.innerWidth;
  const walker = document.createTreeWalker(body, NodeFilter.SHOW_TEXT);
  const range = document.createRange();
  const parts: string[] = [];
  let visibleStart = -1;
  let visibleEnd = -1;
  let offset = 0;

  function isNodeVisible(node: Node): boolean {
    range.selectNodeContents(node);

    for (const rect of range.getClientRects()) {
      if (
        rect.bottom > 0 &&
        rect.top < viewportBottom &&
        rect.right > 0 &&
        rect.left < viewportRight
      ) {
        return true;
      }
    }

    return false;
  }

  let node = walker.nextNode();

  while (node) {
    const value = node.nodeValue ?? '';

    if (value) {
      const previous = parts.at(-1);
      const needsSeparator = previous !== undefined && /\S$/.test(previous) && /^\S/.test(value);
      const piece = needsSeparator ? ` ${value}` : value;

      if (isNodeVisible(node)) {
        if (visibleStart === -1) {
          visibleStart = offset;
        }
        visibleEnd = offset + piece.length;
      }

      parts.push(piece);
      offset += piece.length;
    }

    node = walker.nextNode();
  }

  return { text: parts.join(''), visibleStart, visibleEnd };
}

/**
 * Concatenates the full body text of every currently visible iframe, left to right, plus the
 * [visibleStart, visibleEnd) range within it that's actually on screen. A fixed-layout two-page
 * spread puts two iframes with two different resources on screen at once (see
 * `findVisibleIframes`) — without this, chunking would only ever see the left page's document
 * and the right page would never be read.
 * @param frames - Candidate iframes to consider, e.g. from `ReaderNavigatorService`'s current
 * frame set - NOT every `iframe.readium-navigator-iframe` in the document. Readium keeps a pool
 * of already-rendered frames around for reuse (e.g. chapters visited earlier), and they can keep
 * passing a geometry check even once they're no longer the one actually showing.
 * @returns The visible iframes' concatenated text, plus the visible range within it.
 */
export function getVisibleIframesTextWithRange(frames: HTMLIFrameElement[]): BodyTextVisibleRange {
  let text = '';
  let visibleStart = -1;
  let visibleEnd = -1;

  findVisibleIframes(frames).forEach((iframe) => {
    if (!iframe.contentDocument?.body) return;

    const body = getBodyTextWithVisibleRange(iframe.contentDocument.body);
    if (!body.text) return;

    const separator = text ? ' ' : '';
    const base = text.length + separator.length;

    if (visibleStart === -1 && body.visibleStart !== -1) {
      visibleStart = base + body.visibleStart;
    }
    if (body.visibleEnd !== -1) {
      visibleEnd = base + body.visibleEnd;
    }

    text += separator + body.text;
  });

  return { text, visibleStart, visibleEnd };
}

/**
 * Extracts and returns the visible text fragment from the given document's body.
 * @param body - The root element to walk (typically an iframe's document body).
 * @returns {string} The visible text fragment within that body.
 */
function extractVisibleTextFromBody(body: HTMLElement): string {
  const viewportTop = 0;
  const viewportBottom = window.innerHeight;
  const viewportLeft = 0;
  const viewportRight = window.innerWidth;

  const walker = document.createTreeWalker(body, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      return !node.nodeValue || !node.nodeValue.trim()
        ? NodeFilter.FILTER_SKIP
        : NodeFilter.FILTER_ACCEPT;
    },
  });

  let visibleText = '';
  let currentWord = '';
  let collecting = false;

  const range = document.createRange();

  function isWordChar(char: string): boolean {
    const code = char.charCodeAt(0);
    if (
      (code >= 48 && code <= 57) || // 0-9
      (code >= 65 && code <= 90) || // A-Z
      (code >= 97 && code <= 122)
    ) {
      // a-z
      return true;
    }
    return /\S/.test(char);
  }

  function isRectVisible(rect: DOMRect): boolean {
    return (
      rect.bottom > viewportTop &&
      rect.top < viewportBottom &&
      rect.right > viewportLeft &&
      rect.left < viewportRight
    );
  }

  while (walker.nextNode()) {
    const node = walker.currentNode;
    const text = node.nodeValue;

    range.selectNodeContents(node);
    const nodeRects = range.getClientRects();
    let nodeVisible = false;
    for (const rect of nodeRects) {
      if (isRectVisible(rect)) {
        nodeVisible = true;
        break;
      }
    }

    if (!nodeVisible) {
      if (collecting) {
        visibleText += currentWord + ' ';
        collecting = false;
        currentWord = '';
      }
      continue;
    }
    if (!text) continue;
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const charIsWordChar = isWordChar(char);

      let isVisible = false;

      if (charIsWordChar && !collecting) {
        const isStartOfWord = i === 0 || !isWordChar(text[i - 1]);

        if (isStartOfWord) {
          range.setStart(node, i);
          range.setEnd(node, i + 1);
          const rects = range.getClientRects();

          for (const rect of rects) {
            if (isRectVisible(rect)) {
              isVisible = true;
              break;
            }
          }
        }
      }

      if (charIsWordChar) {
        if (!collecting) {
          const isStartOfWord = i === 0 || !isWordChar(text[i - 1]);

          if (isStartOfWord && isVisible) {
            collecting = true;
            currentWord = char;
          }
        } else {
          currentWord += char;
        }
      } else {
        if (collecting) {
          visibleText += currentWord + char;
        }

        collecting = false;
        currentWord = '';
      }
    }

    if (collecting) {
      visibleText += currentWord + ' ';
      collecting = false;
      currentWord = '';
    }
  }

  return visibleText.trim();
}

/**
 * Extracts and returns the visible text fragment from the currently visible Readium2 iframe(s).
 * A fixed-layout two-page spread shows two iframes at once (see `findVisibleIframes`) — this
 * concatenates both pages' visible text, in reading order, instead of only the left one.
 * @param frames - Candidate iframes to consider - see `getVisibleIframesTextWithRange`'s doc for
 * why this must be the navigator's current frame set, not every iframe in the document.
 * @returns {string} The visible text fragment across all currently visible iframes.
 */
export function getVisibleTextFragment(frames: HTMLIFrameElement[]): string {
  return findVisibleIframes(frames)
    .map((iframe) =>
      iframe.contentDocument?.body ? extractVisibleTextFromBody(iframe.contentDocument.body) : '',
    )
    .filter(Boolean)
    .join(' ')
    .trim();
}

interface TextNodeSpan {
  node: Text;
  start: number;
  end: number;
}

// Walks `body`'s text nodes, inserting a boundary space only where two nodes would run together
// (see `getBodyTextForSpeech`), and records each node's own offset into the concatenated text so a
// found offset range can be mapped back to a precise DOM `Range` afterwards.
function walkTextWithSpans(body: HTMLElement): { text: string; spans: TextNodeSpan[] } {
  const walker = document.createTreeWalker(body, NodeFilter.SHOW_TEXT);
  const parts: string[] = [];
  const spans: TextNodeSpan[] = [];
  let offset = 0;
  let node = walker.nextNode() as Text | null;

  while (node) {
    const value = node.nodeValue ?? '';
    if (value) {
      const previous = parts.at(-1);
      const needsSeparator = previous !== undefined && /\S$/.test(previous) && /^\S/.test(value);
      const piece = needsSeparator ? ` ${value}` : value;
      const start = offset + (needsSeparator ? 1 : 0);
      spans.push({ node, start, end: start + value.length });
      parts.push(piece);
      offset += piece.length;
    }
    node = walker.nextNode() as Text | null;
  }

  return { text: parts.join(''), spans };
}

/** Maps an offset range from `walkTextWithSpans`' text back to a DOM `Range`, if it falls within (a) node(s) that were walked. */
function rangeForOccurrence(
  spans: TextNodeSpan[],
  occurrence: TextOffsetRange,
  doc: Document,
): Range | undefined {
  const startSpan = spans.find((s) => occurrence.start >= s.start && occurrence.start < s.end);
  const endSpan = spans.find((s) => occurrence.end > s.start && occurrence.end <= s.end);
  if (!startSpan || !endSpan) return undefined;

  const range = doc.createRange();
  range.setStart(startSpan.node, occurrence.start - startSpan.start);
  range.setEnd(endSpan.node, occurrence.end - endSpan.start);
  return range;
}

function isRangeVisible(range: Range, win: Window): boolean {
  const viewportBottom = win.innerHeight;
  const viewportRight = win.innerWidth;
  return Array.from(range.getClientRects()).some(
    (rect) =>
      rect.bottom > 0 && rect.top < viewportBottom && rect.right > 0 && rect.left < viewportRight,
  );
}

/** Where a question's anchor sits relative to the page on screen. */
export type AnchorPlacement = 'onPage' | 'offPage' | 'missing';

/** Finds anchors in the chapter document(s) loaded in the visible frames. */
export interface AnchorLocator {
  quote: (quote: TextQuote) => AnchorPlacement;
  /** A legacy (Readium SDK) content CFI, as the previous NubeReader stored in `selection.percent`. */
  cfi: (cfi: string) => AnchorPlacement;
  /**
   * The first text laid out on the page on screen, as a quote. A duplicate earlier in the chapter
   * with the same context could resolve elsewhere, which is why callers verify it with `quote`.
   */
  firstQuoteOnPage: () => TextQuote | undefined;
}

// What is searched in a chapter document: its text with the DOM node each part came from.
interface ChapterTextIndex {
  spans: TextNodeSpan[];
  /** The text as walked: nodes joined, with a space only where two would run together. */
  text: string;
  /** Whitespace-free text, for finding quotes whatever whitespace their source has. */
  compact: CollapsedText;
  /** Whitespace-collapsed text, for building readable quotes; only `firstQuoteOnPage` needs it. */
  collapsed?: CollapsedText;
}

interface IndexedFrame {
  doc: Document;
  win: Window;
  index: ChapterTextIndex;
}

// Each chapter's text is indexed once, not on every page turn: it does not change while the
// chapter is open. This assumes the reader never mutates chapter text nodes (Readium decorations
// are overlays): code that does must drop the entry from here. Keyed by document, so a reloaded
// chapter gets a fresh entry.
const chapterTextIndexes = new WeakMap<Document, ChapterTextIndex>();

function chapterTextIndex(doc: Document, body: HTMLElement): ChapterTextIndex {
  const cached = chapterTextIndexes.get(doc);
  if (cached) return cached;
  const { text, spans } = walkTextWithSpans(body);
  const index: ChapterTextIndex = { spans, text, compact: removeWhitespace(text) };
  // A document still being parsed may have more text to come: do not remember a partial one.
  if (doc.readyState === 'complete') chapterTextIndexes.set(doc, index);
  return index;
}

// Finds `quote` in an indexed chapter ignoring whitespace, since the stored quote (rendered text,
// single spaces) and the chapter's text nodes (pretty-printed XHTML, with newlines) rarely agree on
// it, and maps the match back to a DOM Range.
function findQuoteRange(
  index: ChapterTextIndex,
  quote: TextQuote,
  doc: Document,
): Range | undefined {
  const target: TextQuote = {
    highlight: removeWhitespace(quote.highlight).text,
    before: quote.before && removeWhitespace(quote.before).text,
    after: quote.after && removeWhitespace(quote.after).text,
  };
  if (!target.highlight) return undefined;

  const { compact, spans } = index;
  const occurrence = findQuoteOccurrence(compact.text, target);
  if (!occurrence) return undefined;
  const { rawOffsets } = compact;
  const raw: TextOffsetRange = {
    start: rawOffsets[occurrence.start],
    end: rawOffsets[occurrence.end - 1] + 1,
  };
  return rangeForOccurrence(spans, raw, doc);
}

// Whether `quote` is currently on screen, checking the geometry of its exact matched Range
// rather than the whole (often paragraph-sized) text node it lives in.
export function isQuoteVisible(frames: HTMLIFrameElement[], quote: TextQuote): boolean {
  if (!quote.highlight) return false;

  return findVisibleIframes(frames).some((iframe) => {
    const body = iframe.contentDocument?.body;
    const win = iframe.contentWindow;
    if (!body || !win) return false;

    const doc = body.ownerDocument;
    const range = findQuoteRange(chapterTextIndex(doc, body), quote, doc);
    if (!range) return false;

    return isRangeVisible(range, win);
  });
}

function collapsedText(index: ChapterTextIndex): CollapsedText {
  index.collapsed ??= collapseWhitespace(index.text);
  return index.collapsed;
}

// Where `range` starts relative to `win`'s viewport, taking its start as a point: its first rect
// with any width or, for an empty element or a collapsed space, its first rect with any height. A
// quote that crosses a page break belongs to the page where it starts (in left-to-right books that
// is also where Readium's `go_text` lands). A range with no rendered rect counts as missing, so
// placement falls back to the next anchor.
function rangeStartRect(range: Range): DOMRect | undefined {
  const rects = Array.from(range.getClientRects());
  return (
    rects.find((rect) => rect.right > rect.left) ?? rects.find((rect) => rect.bottom > rect.top)
  );
}

// ColumnSnapper pages are viewport-wide, so only the horizontal offset decides the page; a block
// that overflows its column vertically still belongs to it.
function pagesAwayOf(start: DOMRect, win: Window): number {
  return Math.floor(Math.round(start.left) / win.innerWidth);
}

function placeRangeStart(range: Range, win: Window): AnchorPlacement {
  const start = rangeStartRect(range);
  if (!start) return 'missing';
  const pagesAway = pagesAwayOf(start, win);
  if (pagesAway === 0) return 'onPage';
  // Content laid out beyond the chapter's first or last page (a negative text-indent, a block
  // wider than the column) belongs to that edge page.
  const scroller = win.document.scrollingElement ?? win.document.documentElement;
  const maxScroll = Math.max(0, scroller.scrollWidth - win.innerWidth);
  const scrolled = Math.abs(scroller.scrollLeft);
  const rtl = win.getComputedStyle(scroller).direction === 'rtl';
  const atStart = scrolled <= 1;
  const atEnd = scrolled >= maxScroll - 1;
  const atLeftEdge = rtl ? atEnd : atStart;
  const atRightEdge = rtl ? atStart : atEnd;
  return (pagesAway < 0 && atLeftEdge) || (pagesAway > 0 && atRightEdge) ? 'onPage' : 'offPage';
}

const ANCHOR_QUOTE_LENGTH = 40;

function isRectOnPage(rect: { left: number; right: number }, win: Window): boolean {
  return rect.right > rect.left && Math.floor(Math.round(rect.left) / win.innerWidth) === 0;
}

// Offset, in the walked text, of the first non-space character laid out on the page on screen,
// judged against the frame's own viewport like placeRangeStart.
function firstOnPageOffset({ doc, win, index }: IndexedFrame): number | undefined {
  const range = doc.createRange();
  for (const span of index.spans) {
    range.selectNodeContents(span.node);
    if (!Array.from(range.getClientRects()).some((rect) => isRectOnPage(rect, win))) continue;
    const value = span.node.nodeValue ?? '';
    for (let index = 0; index < value.length; index += 1) {
      if (/\s/.test(value.charAt(index))) continue;
      range.setStart(span.node, index);
      range.setEnd(span.node, index + 1);
      if (Array.from(range.getClientRects()).some((rect) => isRectOnPage(rect, win))) {
        return span.start + index;
      }
    }
  }
  return undefined;
}

// Index of the first entry of the ascending `offsets` that is at least `target`, or -1.
function lowerBound(offsets: number[], target: number): number {
  let low = 0;
  let high = offsets.length;
  while (low < high) {
    const middle = (low + high) >>> 1;
    if (offsets[middle] < target) low = middle + 1;
    else high = middle;
  }
  return low === offsets.length ? -1 : low;
}

// Quote starting at `rawOffset` of the walked text, in collapsed-whitespace form.
function quoteAt({ text, rawOffsets }: CollapsedText, rawOffset: number): TextQuote | undefined {
  const start = lowerBound(rawOffsets, rawOffset);
  if (start === -1) return undefined;
  const highlight = text.slice(start, start + ANCHOR_QUOTE_LENGTH).trimEnd();
  if (!highlight) return undefined;
  return {
    before: text.slice(Math.max(0, start - ANCHOR_QUOTE_LENGTH), start),
    highlight,
    after: text.slice(start + ANCHOR_QUOTE_LENGTH, start + 2 * ANCHOR_QUOTE_LENGTH),
  };
}

/**
 * Locates question anchors in the visible frames' chapter documents. Each chapter's text is walked
 * and indexed at most once (see `chapterTextIndexes`), however many anchors or locators look it up;
 * where the anchors sit on screen is always read from the live layout.
 */
export function createAnchorLocator(frames: HTMLIFrameElement[]): AnchorLocator {
  let indexed: IndexedFrame[] | undefined;
  const indexFrames = (): IndexedFrame[] => {
    indexed ??= findVisibleIframes(frames).flatMap((iframe) => {
      const doc = iframe.contentDocument;
      const win = iframe.contentWindow;
      if (!doc?.body || !win) return [];
      return [{ doc, win, index: chapterTextIndex(doc, doc.body) }];
    });
    return indexed;
  };

  return {
    quote(quote) {
      for (const frame of indexFrames()) {
        const range = findQuoteRange(frame.index, quote, frame.doc);
        if (!range) continue;
        const placement = placeRangeStart(range, frame.win);
        if (placement !== 'missing') return placement;
      }
      return 'missing';
    },
    cfi(cfi) {
      for (const iframe of findVisibleIframes(frames)) {
        const doc = iframe.contentDocument;
        const win = iframe.contentWindow;
        if (!doc || !win) continue;
        const range = resolveContentCfi(doc, cfi);
        if (!range) continue;
        const placement = placeRangeStart(range, win);
        if (placement !== 'missing') return placement;
      }
      return 'missing';
    },
    firstQuoteOnPage() {
      for (const frame of indexFrames()) {
        const offset = firstOnPageOffset(frame);
        const quote =
          offset === undefined ? undefined : quoteAt(collapsedText(frame.index), offset);
        if (quote) return quote;
      }
      return undefined;
    },
  };
}

/**
 * Pagination of a reflowable chapter frame, as Readium's ColumnSnapper sees it. `scrollWidth` and
 * `width` describe the pagination (used by `pageIndexAt`), `scrollLeft` the page on screen (used
 * by `currentPageIndex`).
 */
export interface PaginationGeometry {
  scrollWidth: number;
  width: number;
  /** Absolute horizontal scroll offset (right-to-left books scroll to negative values). */
  scrollLeft: number;
}

export function paginationGeometry(
  frame: HTMLIFrameElement | undefined,
): PaginationGeometry | undefined {
  const win = frame?.contentWindow;
  const doc = frame?.contentDocument;
  const scroller = doc?.scrollingElement ?? doc?.documentElement;
  if (!win || !scroller || win.innerWidth <= 0) return undefined;
  return {
    scrollWidth: scroller.scrollWidth,
    width: win.innerWidth,
    scrollLeft: Math.abs(scroller.scrollLeft),
  };
}

/** The page (screen) currently shown in the frame. */
export function currentPageIndex(geometry: PaginationGeometry): number {
  return Math.round(geometry.scrollLeft / geometry.width);
}

/**
 * The page (screen) a progression lands on, computed the way ColumnSnapper resolves
 * `go_progression`: the offset `progression · (scrollWidth − width)`, nudged by 1px and snapped
 * down to a page start. Page starts Readium reports map back to their own page.
 * `progression` must be a finite number and `geometry.width` positive.
 */
export function pageIndexAt(geometry: PaginationGeometry, progression: number): number {
  const scrollable = Math.max(1, geometry.scrollWidth - geometry.width);
  const offset = Math.min(1, Math.max(0, progression)) * scrollable;
  return Math.floor((offset + 1) / geometry.width);
}

/**
 * Progression to `go_progression` to so a legacy (previous NubeReader) content CFI lands on the
 * page where `createAnchorLocator(frames).cfi` places it: the start of the page its range starts
 * on (clamped to the chapter, mirrored in right-to-left books like placeRangeStart), or the
 * chapter's last page when the CFI does not resolve. Undefined without a paginated visible frame.
 */
export function legacyCfiProgression(frames: HTMLIFrameElement[], cfi: string): number | undefined {
  const frame = findVisibleIframe(frames);
  const geometry = paginationGeometry(frame);
  const doc = frame?.contentDocument;
  const win = frame?.contentWindow;
  if (!geometry || !doc || !win) return undefined;
  const range = resolveContentCfi(doc, cfi);
  const start = range && rangeStartRect(range);
  if (!start) return 1;
  const lastPage = Math.max(0, Math.round(geometry.scrollWidth / geometry.width) - 1);
  const scroller = doc.scrollingElement ?? doc.documentElement;
  const rtl = win.getComputedStyle(scroller).direction === 'rtl';
  const pagesAway = pagesAwayOf(start, win);
  const page = Math.min(
    lastPage,
    Math.max(0, currentPageIndex(geometry) + (rtl ? -pagesAway : pagesAway)),
  );
  return page / Math.max(1, lastPage);
}

/**
 * Extracts the first visible text context from the currently visible Readium iframe.
 * @param frames - Candidate iframes to consider - see `getVisibleIframesTextWithRange`'s doc for
 * why this must be the navigator's current frame set, not every iframe in the document.
 * @returns {LocatorText} An object containing the text context around the first visible word.
 */
function getFirstVisibleTextContext(frames: HTMLIFrameElement[]): LocatorText {
  const viewportTop = 0;
  const viewportBottom = window.innerHeight;
  const viewportLeft = 0;
  const viewportRight = window.innerWidth;

  const iframe: HTMLIFrameElement | undefined = findVisibleIframe(frames);
  if (!iframe?.contentDocument?.body)
    return new LocatorText({ before: '', highlight: '', after: '' });

  const walker = document.createTreeWalker(iframe.contentDocument.body, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      return !node.nodeValue || !node.nodeValue.trim()
        ? NodeFilter.FILTER_SKIP
        : NodeFilter.FILTER_ACCEPT;
    },
  });

  const LIMIT = 40;

  let beforeBuffer = '';
  let highlight = '';
  let afterContext = '';

  let foundStart = false;
  let afterCount = 0;

  let currentWord = '';
  let currentWordVisibleStartIndex = null;
  let _currentWordVisibleAny = false;

  const range = document.createRange();

  function isWordChar(char: string): boolean {
    const code = char.charCodeAt(0);
    if (
      (code >= 48 && code <= 57) || // 0-9
      (code >= 65 && code <= 90) || // A-Z
      (code >= 97 && code <= 122)
    ) {
      // a-z
      return true;
    }
    return /\S/.test(char);
  }

  function isRectVisible(rect: DOMRect): boolean {
    return (
      rect.bottom > viewportTop &&
      rect.top < viewportBottom &&
      rect.right > viewportLeft &&
      rect.left < viewportRight
    );
  }

  while (walker.nextNode()) {
    const node = walker.currentNode;
    const text = node.nodeValue;

    range.selectNodeContents(node);
    const nodeRects = range.getClientRects();
    let nodeVisible = false;
    for (const rect of nodeRects) {
      if (isRectVisible(rect)) {
        nodeVisible = true;
        break;
      }
    }

    if (!nodeVisible) {
      if (currentWord) {
        if (!foundStart) {
          if (currentWordVisibleStartIndex === 0) {
            foundStart = true;
            highlight += currentWord + ' ';
          } else {
            beforeBuffer += currentWord + ' ';
            if (beforeBuffer.length > LIMIT) {
              beforeBuffer = beforeBuffer.slice(-LIMIT);
            }
          }
        } else if (highlight.length < LIMIT) {
          highlight += currentWord + ' ';
        } else if (afterCount < LIMIT) {
          afterContext += currentWord + ' ';
          afterCount += currentWord.length + 1;
        }

        currentWord = '';
        currentWordVisibleStartIndex = null;
        _currentWordVisibleAny = false;
      }
      continue;
    }
    if (!text) continue;
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const charIsWordChar = isWordChar(char);
      const prevChar = i > 0 ? text[i - 1] : ' ';
      const isStartOfWord = charIsWordChar && !isWordChar(prevChar);

      let isVisible = false;

      if (charIsWordChar) {
        range.setStart(node, i);
        range.setEnd(node, i + 1);
        const rects = range.getClientRects();

        for (const rect of rects) {
          if (isRectVisible(rect)) {
            isVisible = true;
            break;
          }
        }
      }

      if (isStartOfWord) {
        currentWord = '';
        currentWordVisibleStartIndex = null;
        _currentWordVisibleAny = false;
      }

      if (charIsWordChar) {
        currentWord += char;
        if (isVisible) {
          _currentWordVisibleAny = true;
          if (currentWordVisibleStartIndex === null) {
            currentWordVisibleStartIndex = currentWord.length - 1;
          }
        }
      } else {
        if (currentWord) {
          if (!foundStart) {
            if (currentWordVisibleStartIndex === 0) {
              foundStart = true;
              highlight += currentWord + char;
            } else {
              beforeBuffer += currentWord + char;
              if (beforeBuffer.length > LIMIT) {
                beforeBuffer = beforeBuffer.slice(-LIMIT);
              }
            }
          } else if (highlight.length < LIMIT) {
            highlight += currentWord + char;
          } else if (afterCount < LIMIT) {
            afterContext += currentWord + char;
            afterCount += currentWord.length + 1;
          }

          currentWord = '';
          currentWordVisibleStartIndex = null;
          _currentWordVisibleAny = false;
        }
      }

      if (foundStart && afterCount >= LIMIT) {
        return new LocatorText({
          before: beforeBuffer.trimStart(),
          highlight: highlight.trim(),
          after: afterContext.trimEnd(),
        });
      }
    }

    if (currentWord) {
      if (!foundStart) {
        if (currentWordVisibleStartIndex === 0) {
          foundStart = true;
          highlight += currentWord + ' ';
        } else {
          beforeBuffer += currentWord + ' ';
          if (beforeBuffer.length > LIMIT) {
            beforeBuffer = beforeBuffer.slice(-LIMIT);
          }
        }
      } else if (highlight.length < LIMIT) {
        highlight += currentWord + ' ';
      } else if (afterCount < LIMIT) {
        afterContext += currentWord + ' ';
        afterCount += currentWord.length + 1;
      }

      currentWord = '';
      currentWordVisibleStartIndex = null;
      _currentWordVisibleAny = false;
    }

    if (foundStart && afterCount >= LIMIT) {
      break;
    }
  }

  return new LocatorText({
    before: beforeBuffer.trimStart(),
    highlight: highlight.trim(),
    after: afterContext.trimEnd(),
  });
}

// Función para obtener el contexto de la selección usando TreeWalker
export function getSelectionContext(
  numWords = 10,
  selection = window.getSelection(),
): SelectionContext | undefined {
  if (!selection || !selection.rangeCount || selection.toString().trim() === '') {
    return undefined;
  }

  const range = selection.getRangeAt(0);
  const selectedText = selection.toString();

  // Obtener el nodo raíz común (normalmente el body o un contenedor)
  const rootNode = range.commonAncestorContainer.ownerDocument?.body;

  if (!rootNode) return undefined;

  // Obtener texto antes de la selección
  const textBefore = getTextBefore(rootNode, range.startContainer, range.startOffset);

  // Obtener texto después de la selección
  const textAfter = getTextAfter(rootNode, range.endContainer, range.endOffset);

  // Extraer las últimas N palabras antes
  const wordsBefore = extractLastWords(textBefore, numWords);

  // Extraer las primeras N palabras después
  const wordsAfter = extractFirstWords(textAfter, numWords);

  return {
    selectedText: selectedText,
    before: wordsBefore,
    after: wordsAfter,
    fullContext: `${wordsBefore} ${selectedText} ${wordsAfter}`,
  };
}

// Obtener todo el texto ANTES del punto de inicio de la selección
function getTextBefore(rootNode: Node, startNode: Node, startOffset: number) {
  let textBefore = '';
  let reachedStart = false;

  // Crear TreeWalker para recorrer todos los nodos de texto
  const walker = document.createTreeWalker(rootNode, NodeFilter.SHOW_TEXT, null);

  let currentNode;
  const textNodes = [];

  // Recopilar todos los nodos de texto hasta el punto de inicio
  while ((currentNode = walker.nextNode())) {
    if (currentNode === startNode) {
      // Agregar solo la parte antes del offset
      textNodes.push(currentNode.textContent?.substring(0, startOffset));
      reachedStart = true;
      break;
    } else {
      textNodes.push(currentNode.textContent);
    }
  }

  return textNodes.join('');
}

// Obtener todo el texto DESPUÉS del punto final de la selección
function getTextAfter(rootNode: Node, endNode: Node, endOffset: number) {
  let textAfter = '';
  let foundEnd = false;

  // Crear TreeWalker para recorrer todos los nodos de texto
  const walker = document.createTreeWalker(rootNode, NodeFilter.SHOW_TEXT, null);

  let currentNode;
  const textNodes = [];

  // Buscar el nodo final y empezar a recopilar desde ahí
  while ((currentNode = walker.nextNode())) {
    if (foundEnd) {
      textNodes.push(currentNode.textContent);
    } else if (currentNode === endNode) {
      // Agregar solo la parte después del offset
      const afterText = currentNode.textContent?.substring(endOffset);
      if (afterText) {
        textNodes.push(afterText);
      }
      foundEnd = true;
    }
  }

  return textNodes.join('');
}

// Extraer las últimas N palabras de un texto
function extractLastWords(text: string, numWords: number) {
  const words = text
    .trim()
    .split(/\s+/)
    .filter((w) => w.length > 0);
  return words.slice(-numWords).join(' ');
}

// Extraer las primeras N palabras de un texto
function extractFirstWords(text: string, numWords: number) {
  const words = text
    .trim()
    .split(/\s+/)
    .filter((w) => w.length > 0);
  return words.slice(0, numWords).join(' ');
}
